import { api } from '@/lib/axios';

// Enums
export type ConversationStatus = 'OPEN' | 'WAITING' | 'IN_PROGRESS' | 'CLOSED';
export type ConversationPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
export type MessageDirection = 'INBOUND' | 'OUTBOUND';
export type ChatMessageType = 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'DOCUMENT' | 'LOCATION' | 'CONTACT' | 'STICKER' | 'SYSTEM';
export type MessageDeliveryStatus = 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';

export interface ConversationTag {
  id: string;
  name: string;
  color?: string;
}

// Interfaces
export interface ConversationClient {
  id: string;
  name: string;
  phone: string;
}

export interface ConversationDepartment {
  id: string;
  name: string;
}

export interface ConversationUser {
  id: string;
  name: string;
  email: string;
}

export interface LastMessage {
  id: string;
  content: string;
  messageType: ChatMessageType;
  direction: MessageDirection;
  createdAt: string;
}

export interface Conversation {
  id: string;
  tenantId: string;
  clientId: string;
  departmentId?: string;
  assignedUserId?: string;
  whatsappPhone: string;
  status: ConversationStatus;
  priority: ConversationPriority;
  unreadCount: number;
  lastMessageAt?: string;
  startedAt: string;
  closedAt?: string;
  closedReason?: string;
  createdAt: string;
  updatedAt: string;
  client: ConversationClient;
  department?: ConversationDepartment;
  assignedUser?: ConversationUser;
  lastMessage?: LastMessage;
  tags?: ConversationTag[];
}

export interface ChatMessage {
  id: string;
  tenantId: string;
  conversationId: string;
  direction: MessageDirection;
  senderUserId?: string;
  messageType: ChatMessageType;
  content: string;
  mediaUrl?: string;
  mediaType?: string;
  mediaFileName?: string;
  whatsappMessageId?: string;
  whatsappTimestamp?: string;
  status: MessageDeliveryStatus;
  deliveredAt?: string;
  readAt?: string;
  failedAt?: string;
  failureReason?: string;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  senderUser?: ConversationUser;
}

export interface ConversationQueryDto {
  departmentId?: string;
  status?: ConversationStatus;
  assignedUserId?: string;
  unreadOnly?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface MessageQueryDto {
  before?: string;
  limit?: number;
}

export interface SendMessageDto {
  content: string;
  messageType?: ChatMessageType;
  mediaUrl?: string;
  mediaType?: string;
  mediaFileName?: string;
}

export interface TransferConversationDto {
  departmentId: string;
  userId?: string;
  reason?: string;
}

export interface ConversationsResponse {
  conversations: Conversation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface MessagesResponse {
  messages: ChatMessage[];
  hasMore: boolean;
  oldestId: string | null;
}

export interface ConversationStats {
  total: number;
  open: number;
  waiting: number;
  inProgress: number;
  closed: number;
  unread: number;
}

export const chatService = {
  // Conversas
  async getConversations(params: ConversationQueryDto = {}): Promise<ConversationsResponse> {
    const response = await api.get<ConversationsResponse>('/webchat/conversations', { params });
    return response.data;
  },

  async getConversationById(id: string): Promise<Conversation> {
    const response = await api.get<Conversation>(`/webchat/conversations/${id}`);
    return response.data;
  },

  async getConversationStats(): Promise<ConversationStats> {
    const response = await api.get<ConversationStats>('/webchat/stats');
    return response.data;
  },

  // Mensagens
  async getMessages(conversationId: string, params: MessageQueryDto = {}): Promise<MessagesResponse> {
    const response = await api.get<MessagesResponse>(
      `/webchat/conversations/${conversationId}/messages`,
      { params }
    );
    return response.data;
  },

  async sendMessage(conversationId: string, data: SendMessageDto): Promise<ChatMessage> {
    const response = await api.post<ChatMessage>(
      `/webchat/conversations/${conversationId}/messages`,
      data
    );
    return response.data;
  },

  // Ações de conversa
  async claimConversation(conversationId: string): Promise<Conversation> {
    const response = await api.post<Conversation>(
      `/webchat/conversations/${conversationId}/claim`
    );
    return response.data;
  },

  async releaseConversation(conversationId: string): Promise<Conversation> {
    const response = await api.post<Conversation>(
      `/webchat/conversations/${conversationId}/release`
    );
    return response.data;
  },

  async transferConversation(conversationId: string, data: TransferConversationDto): Promise<Conversation> {
    const response = await api.post<Conversation>(
      `/webchat/conversations/${conversationId}/transfer`,
      data
    );
    return response.data;
  },

  async closeConversation(conversationId: string, reason?: string): Promise<Conversation> {
    const response = await api.post<Conversation>(
      `/webchat/conversations/${conversationId}/close`,
      { reason }
    );
    return response.data;
  },

  async reopenConversation(conversationId: string): Promise<Conversation> {
    const response = await api.post<Conversation>(
      `/webchat/conversations/${conversationId}/reopen`
    );
    return response.data;
  },

  async markAsRead(conversationId: string): Promise<void> {
    await api.post(`/webchat/conversations/${conversationId}/read`);
  },

  // Historico
  async importHistory(conversationId: string): Promise<{ imported: number }> {
    const response = await api.post<{ imported: number }>(
      `/webchat/conversations/${conversationId}/import-history`
    );
    return response.data;
  },

  // Iniciar nova conversa
  async startConversation(clientId: string): Promise<Conversation> {
    const response = await api.post<Conversation>(
      '/webchat/conversations/start',
      { clientId }
    );
    return response.data;
  },

  // Tags
  async getConversationTags(conversationId: string): Promise<ConversationTag[]> {
    const response = await api.get<ConversationTag[]>(
      `/webchat/conversations/${conversationId}/tags`
    );
    return response.data;
  },

  async addTag(conversationId: string, tagId: string): Promise<void> {
    await api.post(`/webchat/conversations/${conversationId}/tags/${tagId}`);
  },

  async removeTag(conversationId: string, tagId: string): Promise<void> {
    await api.delete(`/webchat/conversations/${conversationId}/tags/${tagId}`);
  },

  async updateTags(conversationId: string, tagIds: string[]): Promise<ConversationTag[]> {
    const response = await api.put<ConversationTag[]>(
      `/webchat/conversations/${conversationId}/tags`,
      { tagIds }
    );
    return response.data;
  },
};
